"use client";

import type { ProcessedSet, Review } from "@workspace/types";
import { memo } from "react";
import { CalendarSetCard } from "@/components/features/festival/calendar/calendar-set";
import { useRealtimeSetReview } from "@/hooks/features/festival/use-realtime-set-review";
import { useCurrentUser } from "@/stores";

interface RealtimeCalendarSetCardProps {
	set: ProcessedSet;
	review?: Review | null; // undefined = parent didn't batch, null = no review for this set
	savedSchedule: string[];
	festivalSlug: string;
	variant?: "personal" | "public";
	onStartReview?: (setId: string) => void;
	onToggleSetInSchedule?: (setId: string) => void;
	topOffset: string;
	setHeight: string;
	readOnly?: boolean;
	onAuthRequired?: () => void;
	isHorizontal?: boolean;
}

/**
 * Real-time wrapper for CalendarSetCard
 * - For current user: Subscribes to real-time set review updates
 * - For other users: Uses the review passed from parent (no realtime)
 * - Provides instant cross-tab synchronization when applicable
 */
export const RealtimeCalendarSetCard = memo(function RealtimeCalendarSetCard({
	set,
	review: externalReview,
	readOnly,
	...props
}: RealtimeCalendarSetCardProps) {
	const currentUser = useCurrentUser();

	// Determine if parent provided review data for this set
	// undefined = parent didn't batch reviews → use per-set realtime hook
	// null = parent explicitly says no review for this set → trust parent
	// Review object = parent batched this review → trust parent
	const parentProvided = externalReview !== undefined;
	const shouldUseRealtime = !parentProvided && !!currentUser?.id && !readOnly;

	// Subscribe to real-time review updates for this set (only if parent didn't batch)
	const { review: realtimeReview } = useRealtimeSetReview({
		setId: set.id,
		artistId: set.artist?.id || "",
		userId: currentUser?.id || null,
		enabled: shouldUseRealtime,
	});

	// Parent-provided data is authoritative (even null = no review); fall back to per-set hook
	const reviewToUse = parentProvided ? externalReview : (shouldUseRealtime ? realtimeReview : null);

	// Transform review to match expected format
	const transformedReview = reviewToUse
		? {
				setId: reviewToUse.setId,
				artistId: reviewToUse.artistId,
				rating: reviewToUse.rating,
				content: reviewToUse.content,
				loved: reviewToUse.loved,
				sawLive: reviewToUse.sawLive,
				sawStream: reviewToUse.sawStream,
				wantToSee: reviewToUse.wantToSee,
				mustSee: reviewToUse.mustSee,
			}
		: undefined;

	return (
		<CalendarSetCard
			set={set}
			review={transformedReview}
			readOnly={readOnly}
			{...props}
		/>
	);
});
